import {
	hasRequestObservationSubscribers,
	normalizeRequestRoute,
	publishRequestObservation,
	requestObservationDuration,
	requestObservationNow,
	type RequestObservationOutcome,
} from './request-observer';

export const getTraceIdFromResponse = (response: Response | undefined): string | undefined =>
	response?.headers?.get('x-trace-id') ??
	response?.headers?.get('atl-traceid') ??
	response?.headers?.get('x-b3-traceid') ??
	undefined;

export interface RovoInstrumentedError extends Error {
	errorAttributes: {
		errorCode?: string;
		statusCode?: number;
		rawErrorMessage?: string;
		traceId?: string;
		requestId?: string;
		[key: string]: string | number | undefined;
	};
}

export class FetchError extends Error implements RovoInstrumentedError {
	constructor(
		message: string,
		public errorAttributes: {
			statusCode?: number;
			traceId?: string;
			requestId?: string;
			rawErrorMessage?: string;
			errorCode?: string;
		},
	) {
		super(message);
		this.name = 'FetchError';
	}
}

type FetchOptions = {
	path: string;
	options: RequestInit;
};

class HttpApiError extends Error implements RovoInstrumentedError {
	constructor(
		public message: string,
		public errorAttributes: {
			statusCode: number;
			traceId?: string;
			requestId?: string;
			rawErrorMessage?: string;
			errorCode?: string;
		},
	) {
		super(message);
	}
}

export const performHttpFetch = async <T = unknown>({
	path,
	options,
}: FetchOptions): Promise<T> => {
	const startedAt = hasRequestObservationSubscribers() ? requestObservationNow() : undefined;
	try {
		const response = await fetch(path, options);
		if (!response.ok || response.status >= 400) {
			await processHttpErrorResponse({ response });
		}
		const result = (await response.json()) as T;
		if (startedAt !== undefined) {
			publishRequestObservation({
				kind: 'http',
				method: (options.method ?? 'GET').toUpperCase(),
				route: normalizeRequestRoute(path),
				durationMs: requestObservationDuration(startedAt),
				outcome: 'success',
				status: response.status,
			});
		}
		return result;
	} catch (err) {
		const outcome: RequestObservationOutcome =
			err instanceof HttpApiError
				? 'httpError'
				: err instanceof Error && err.name === 'AbortError'
					? 'abort'
					: 'networkError';
		const statusCode = err instanceof HttpApiError ? err.errorAttributes.statusCode : undefined;
		if (startedAt !== undefined) {
			publishRequestObservation({
				kind: 'http',
				method: (options.method ?? 'GET').toUpperCase(),
				route: normalizeRequestRoute(path),
				durationMs: requestObservationDuration(startedAt),
				outcome,
				status: statusCode,
			});
		}
		const originalErrorMessage =
			err instanceof Error ? 'Original error: ' + err.message : 'Unknown error';
		throw new FetchError(`performHttpFetch error while fetching ${path}. ${originalErrorMessage}`, {
			errorCode: 'performHttpFetchError',
			statusCode,
		});
	}
};

const processHttpErrorResponse = async ({
	response,
}: {
	response: Response;
}): Promise<Response> => {
	throw new HttpApiError(`[Code ${response.status}] ${await response.text()}`, {
		statusCode: response.status,
		traceId: getTraceIdFromResponse(response),
	});
};
